import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, CheckCircle2, AlertTriangle, Heart } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/config/api';

const ParentSignupPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invite, setInvite] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', password: '', confirm: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/parent-signup/${token}`);
        setInvite(res.data);
        setForm(f => ({ ...f, name: res.data.name || '' }));
      } catch (e) {
        setError(e.response?.data?.detail || 'This invitation link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error('Password must be at least 8 characters');
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/parent-signup/${token}/complete`, {
        password: form.password, name: form.name, phone: form.phone,
      });
      // Store token; do a full navigation to /dashboard so AuthContext hydrates fresh.
      localStorage.setItem('token', res.data.token);
      toast.success('Welcome — your account is ready!');
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Link Not Available</h2>
            <p className="text-muted-foreground text-sm mb-4">{error}</p>
            <Button variant="outline" onClick={() => navigate('/login')}>Go to login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-slate-100 p-4">
      <Card className="max-w-md w-full" data-testid="parent-signup-card">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center mb-3">
            <Heart className="w-8 h-8 text-primary" />
          </div>
          <CardTitle className="text-2xl font-heading">Welcome to {invite.center_name}</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Set up your parent account</p>
        </CardHeader>
        <CardContent>
          {invite.child_names?.length > 0 && (
            <div className="mb-6 p-3 bg-teal-50 rounded-lg border border-teal-100">
              <p className="text-xs uppercase font-semibold text-teal-700 mb-1">You&apos;ll be linked to:</p>
              <p className="text-sm text-teal-900">{invite.child_names.join(', ')}</p>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-xs">Email (locked)</Label>
              <Input value={invite.email} disabled className="bg-muted" data-testid="signup-email" />
            </div>
            <div>
              <Label htmlFor="signup-name" className="text-xs">Your name</Label>
              <Input
                id="signup-name"
                value={form.name}
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="First and last name"
                data-testid="signup-name"
              />
            </div>
            <div>
              <Label htmlFor="signup-phone" className="text-xs">Phone (optional)</Label>
              <Input
                id="signup-phone"
                value={form.phone}
                onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="(555) 555-5555"
                data-testid="signup-phone"
              />
            </div>
            <div>
              <Label htmlFor="signup-password" className="text-xs">Create a password</Label>
              <Input
                id="signup-password"
                type="password"
                value={form.password}
                onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                placeholder="At least 8 characters"
                required
                minLength={8}
                data-testid="signup-password"
              />
            </div>
            <div>
              <Label htmlFor="signup-confirm" className="text-xs">Confirm password</Label>
              <Input
                id="signup-confirm"
                type="password"
                value={form.confirm}
                onChange={(e) => setForm(f => ({ ...f, confirm: e.target.value }))}
                required
                minLength={8}
                data-testid="signup-confirm"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="signup-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Create My Account
            </Button>
          </form>
          <p className="text-xs text-center text-muted-foreground mt-4">
            By creating an account you agree to your center&apos;s parent portal terms.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ParentSignupPage;
